/**
 * Payment (processor)-related strings.
 */
const Contact = <b>Contact</b>;
const Lead = <b>Lead</b>;
const Case = <b>Case</b>;
const Contacts = <b>Contacts</b>;
const Leads = <b>Leads</b>;
const Cases = <b>Cases</b>;

type ProcessorParam = {processorName: string};

export default {
	d_title: 'Payment Methods',
	h_title: (params: ProcessorParam) => <div>
		<p>Once connected to {params.processorName}, this section will appear in {Contacts}, {Leads} and {Cases}.</p>
		<p>It will show the payment methods for the client or clients related to this {Contact}, {Lead} or {Case}.</p>
		<p>Two types of payments are accepted, <b>credit card</b> and <b>eCheck</b>.</p>
		<p>These payment methods will then be available whenever you take a payment or replenish a trust.</p>
	</div>,

	d_infoRequired: 'Not Eligible for Payment Processing',
	d_notAPayer: 'Not a client or benefactor in a lead or case',
	d_needFullName: 'Needs full name info',
	d_needEmail: 'Needs a primary/contactable email',
	d_needAddress: 'Needs a primary/contactable address',
	d_needPhone: 'Needs a primary/contactable phone number',

	d_payments: 'Payments',
	h_payments: <div>
		<p>This section is used to connect your LawPay account to Outlaw Practice.</p>
		<p>Outlaw Practice provides deep integration with LawPay. Once connected, you will see <b>Payment
			Methods</b> sections in your {Contacts}, {Leads} and {Cases}. You can enter credit card and ACH information
			for each client or benefactor, with the payment information being stored at the payment gateway (your
			information is not stored in Outlaw Practice or Lawpay, but rather with the gateway itself).
		</p>
	</div>,

	d_applicationReview: (params: { processorName: string, wait: string }) => `Your application is being reviewed by ${params.processorName}, which can take ${params.wait}.`,
	h_applicationReview: (params: ProcessorParam) => <div>
		<p>You will get an email from {params.processorName} when your application is approved, and Outlaw Practice will
			have finished setup for you.
		</p>

		<p>If you canceled your application before you finished, click the link below and you can reapply.</p>
	</div>,
	d_applying: (params: { processor: string }) => `${params.processor} applicationId`,
	d_canceled: 'I Canceled My Application',
	d_connect: (params: ProcessorParam) => `Connect with ${params.processorName}`,
	d_connected: 'connected',
	d_connectFailure: (params: { name: string }) => `Unable to sync contacts with ${params.name}. Wait a minute, then click on the Sync Contacts with ${params.name} button`,
	d_connectSuccess: (params: { name: string }) => `You have successfully been connected to ${params.name}. Please wait a moment for the server to process the request fully.`,
	d_disconnect: (params: ProcessorParam) => `Disconnect from ${params.processorName}`,
	d_disconnected: 'disconnected',
	d_disconnecting: (params: ProcessorParam) => `Disconnecting from ${params.processorName}`,
	d_eCheck: 'E-Check',
	d_eCheckTrust: 'E-Check (Trust)',
	d_forceSync: (params: ProcessorParam) => `Force Contact Sync with ${params.processorName}`,
	d_merchantTrust: 'Trust (Merchant)',
	d_merchantOperating: 'Operating (Merchant)',
	d_pleaseWait: 'Hold On',
	d_redirecting: (params: ProcessorParam) => `Redirecting to ${params.processorName} website`,
	d_signup: (params: ProcessorParam) => `Sign Up with ${params.processorName}`,
	d_syncing: (params: ProcessorParam) => `Syncing Contacts with ${params.processorName}`,
	d_unknownProcessor: (params: ProcessorParam) => `Error: Unknown Processor ${params.processorName}`,

	// Common form fields
	d_errNeedCvv: 'Need a CVV code',
	d_errNeedEmail: 'Need an email address',
	d_errNeedAddress: 'Need a physical address',
	d_description: 'Description',
	h_description: `<div><p>Use the description field to keep track of payment methods.</p><p>For example, you could say "Linda's personal card", or "Maxed out, wait until next month".</p></div>`,

	// Credit Card form
	d_errNeedCardNumber: 'A card number is required',
	d_errCardNumberTooShort: 'The card number is not valid (too short)',
	d_errNeedExpMonth: 'Need an expiration month (MM)',
	d_errNeedExpYear: 'Need an expiration year (YYYY)',
	d_errExpPast: 'The expiration date is in the past',
	d_errNeedName: 'Need a name on the card',
	d_errNeedFirst: 'Need a first name',
	d_errNeedLast: 'Need a last name',
	d_cardNumber: 'Card #',
	d_expMonth: 'Expires Month',
	d_expYear: 'Expires Year',
	d_cvv: 'CVV',
	h_cvv: 'The 3 or 4 digit code, usually found on the back of the card.',
	d_name: 'Name',
	h_name: 'The full name of the card user, usually on the front of the card.',
	d_email: 'Email',
	h_email: 'LawPay requires an email address. This is used to send transaction notices.',
	d_phone: 'Phone Number',
	h_phone: 'Providing a phone number associated with the card lowers the transaction rates you pay for each charge!',
	d_address: 'Address',
	h_address: '<div><p>LawPay requires a physical mailing address for the card.</p><p>Outlaw Practice will use the primary address for the contact, but you can override this with any address you have for the contact.</p></div>',

	// Bank Account form
	d_errRouting: 'Need a routing number',
	d_errAccount: 'Need an account number',
	d_errAcctType: 'Need an account type',
	d_routingNumber: 'Routing #',
	h_routingNumber: 'The routing number for the bank.',
	d_accountNumber: 'Account #',
	h_accountNumber: 'The account number for the bank account',
	d_firstName: (params: {isCompany: boolean}) => params.isCompany ? 'Name' : 'First Name',
	h_firstName: `LawPay requires the account owner's first and last name.`,
	d_lastName: (params: {isCompany: boolean}) => params.isCompany ? '' : 'Last Name',
	h_lastName: `LawPay requires the account owner's first and last name.`,

	d_errInvalidCardNumber: 'Invalid card number (failed checksum validation)',

	d_businessName: 'Business Name',
	h_businessName: '<div><p>For a business account, give the name of the business or the full name of an authorized check writer.</p></div>',

	d_accountType: 'Account Type',
	h_accountType: '<div><p>There are two types of accounts, <b>CHECKING</b> and <b>SAVINGS.</b></p></div>',

	d_accountHolderType: 'Account Holder',
	h_accountHolderType: '<div><p>There are two types of account holders, <b>INDIVIDUAL</b> and <b>BUSINESS</b>.</p><p>If this is a personal account (checking or savings), choose INDIVIDUAL.</p><p>If this is a business account (checking or savings), choose BUSINESS.</p></div>',

	// Lawpay connection error
	d_lawpayConnectError: 'Error connecting existing LawPay accounts: ',
	d_outlawConnectError: 'Error connecting your Outlaw Payment accounts: ',

	// Email Receipts
	d_emailReceipts: 'Email Receipts',
	h_emailReceipts: '<div><p>Configure who receives email receipts when a payment is processed.</p><p>You can send receipts to the payer, the client on the case, or to your own firm email address.</p></div>',

	// Payment Portal
	d_paymentPortal: 'Payment Portal',
	h_paymentPortal: '<div><div>Enabling the Payment Portal will include a link and barcode on your invoices that takes the client to a quick, easy payment page where they can pay their current amount due and/or deposit money into their trust account to meet minimum trust balances. Benefactors can also be sent an email with a payment link.</div><div>Payments made by the client or benefactors will immediately be visible throughout Outlaw Practice.</div><div>You can decide whether benefactors (payers other than the client) will get an email with a payment link, or with the invoice attached. In either case, the notes from the invoice will be included in the email.</div><div>You can also specify an email address to CC for all sent invoices.</div></div>',

	// Payouts
	d_payouts: 'Payouts',
	h_payouts: '<div><p>Same-day payouts move funds from settled card payments to your bank account on the same day, instead of the standard next-business-day timing.</p><p>The payment processor charges 0.1% of the payout amount for same-day funding. Leave this off to avoid that fee and use standard payouts.</p></div>',

	d_sameDayPayout: 'Enable same-day payouts (0.1% fee on each payout)',

	d_enablePaymentPortal: 'Enable the Payment Portal',
	d_sendPaymentLinkToBenefactors: 'Send payment link to benefactor(s)?',
	d_sendInvoiceToBenefactors: 'Send invoice contents to benefactor(s)?',
	d_sendInvoiceToFirm: 'CC emailed invoice to the firm?',

	d_sendEmailClient: 'Send an email receipt to the client',
	d_sendEmailPayer: 'Send an email receipt to the payer (if not the client)',
	d_sendEmailSelf: 'Send an email receipt to yourself',

	d_emailSetup: 'Send an email receipt to yourself',
	d_emailTo: 'To',
	d_emailCC: 'CC',
	d_emailBCC: 'BCC',
	d_emailReplyTo: 'Reply-To',

	d_dashboard: 'Payments Dashboard',
	h_dashboard: '<div><b>Dashboard for Outlaw Payments</b><ul><li>View your merchant account balance</li><li>View your transaction history</li><li>View your upcoming and past withdrawals</li><li>View and download statements</li><li>View the status of your disputes</li></ul></div>',

	d_paymentId: 'ID',
	h_paymentId: '<div><p>This is the unique ID for this payment in Outlaw Payments.</p><p>If you ever have a question about a payment, reference this ID.</p></div>',

	d_paymentStart: 'Run On',
	h_paymentStart: '<div><p>This is the date the payout was processed.</p><p>Note that payouts are processed at 4pm EST, so any transactions posted after that time will not show up until the next payout cycle (Monday - Friday, excluding bank holidays).</p></div>',

	d_paymentAmount: 'Amount',

	d_paymentAccount: 'Account',
	h_paymentAccount: '<div><p>The account, Trust or Operating, where the funds were deposited.</p><p>Each day, one payout is run for Trust, and one for Operating, if a sufficient total dollar amount is available for a payout.</p><p>To avoid </p></div>'
};